import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDownLeft,
  ArrowUpRight,
  Bell,
  CheckCircle2,
  Clock3,
  Eye,
  FileText,
  Inbox,
  Loader2,
  RefreshCw,
  Search,
  Send,
  Share2,
  ShieldCheck,
  UsersRound,
  X,
  XCircle,
} from 'lucide-react';
import {
  WorkspaceBackButton,
  WorkspacePage,
  WorkspaceSidebar,
  WorkspaceStageCard,
} from '../shared/WorkspaceChrome';
import { API_BASE_URL, API_ENDPOINTS } from '../../constants/api';
import { apiFetch } from '../../utils/apiClient';
import { buildApplicationsPath } from '../../utils/applicationRoutes';

const REQUEST_FILTERS = [
  { id: 'all', label: 'All', summaryKey: 'total_pending' },
  { id: 'brd', label: 'BRD', summaryKey: 'brd_pending' },
  { id: 'sharing', label: 'Sharing', summaryKey: 'sharing_pending' },
  { id: 'testcases', label: 'Test Cases', summaryKey: 'test_case_pending' },
];

const REQUEST_VIEWS = [
  { id: 'pending', label: 'Pending', icon: Inbox },
  { id: 'history', label: 'History', icon: Clock3 },
];

const KIND_META = {
  brd_approval: {
    label: 'BRD Approval',
    tileLabel: 'BRD approval',
    icon: FileText,
    badgeClass: 'request-badge-blue',
    cardClass: 'request-card-blue',
    summaryLabel: 'BRD',
  },
  brd_share_request: {
    label: 'Sharing Request',
    tileLabel: 'Sharing request',
    icon: Share2,
    badgeClass: 'request-badge-emerald',
    cardClass: 'request-card-emerald',
    summaryLabel: 'Sharing',
  },
  test_case_approval: {
    label: 'Test-case Approval',
    tileLabel: 'Test review',
    icon: CheckCircle2,
    badgeClass: 'request-badge-amber',
    cardClass: 'request-card-amber',
    summaryLabel: 'Test Cases',
  },
};

const STATUS_META = {
  pending_approval: {
    label: 'Pending Approval',
    badgeClass: 'request-status-blue',
  },
  pending_review: {
    label: 'Pending Review',
    badgeClass: 'request-status-amber',
  },
  pending: {
    label: 'Pending',
    badgeClass: 'request-status-slate',
  },
  approved: {
    label: 'Approved',
    badgeClass: 'request-status-emerald',
  },
  rejected: {
    label: 'Rejected',
    badgeClass: 'request-status-rose',
  },
  changes_requested: {
    label: 'Changes Requested',
    badgeClass: 'request-status-orange',
  },
};

const INITIAL_SUMMARY = {
  brd_pending: 0,
  sharing_pending: 0,
  test_case_pending: 0,
  total_pending: 0,
};

const HISTORY_PAGE_SIZE = 12;
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const joinClasses = (...classes) => classes.filter(Boolean).join(' ');

const normalizeCount = (value) => {
  const count = Number(value);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
};

const formatDateTime = (value) => {
  if (!value) return 'Unknown time';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Unknown time';
  return parsed.toLocaleString();
};

const formatShortDate = (value) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

const toDateTimeAttribute = (value) => {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
};

const toKindMeta = (kind) => KIND_META[String(kind || '').toLowerCase()] || {
  label: 'Request',
  tileLabel: 'Request',
  icon: Bell,
  badgeClass: 'request-badge-slate',
  cardClass: 'request-card-slate',
  summaryLabel: 'Other',
};

const toStatusMeta = (status) => STATUS_META[String(status || '').toLowerCase()] || {
  label: 'Updated',
  badgeClass: 'request-status-slate',
};

const readString = (...values) => {
  for (const value of values) {
    const normalized = String(value || '').trim();
    if (normalized) return normalized;
  }
  return '';
};

const readPositiveNumber = (...values) => {
  for (const value of values) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }
  return 0;
};

const getRequestKind = (item) => (
  readString(item?.kind, item?.link?.kind, item?.metadata?.kind).toLowerCase()
);

const getPdfId = (item) => (
  readString(item?.pdf_id, item?.link?.pdf_id, item?.metadata?.pdf_id)
);

const getSectionNumber = (item) => (
  readPositiveNumber(
    item?.section_number,
    item?.link?.section_number,
    item?.metadata?.section_number
  )
);

const getRequestId = (item) => (
  readString(item?.request_id, item?.metadata?.request_id, item?.id)
);

const getRequestTitle = (item) => (
  readString(
    item?.title,
    item?.section_title,
    item?.metadata?.section_title,
    item?.pdf_file,
    item?.metadata?.pdf_file,
    getPdfId(item)
  ) || 'Request'
);

const getRequestPerson = (item, prefix) => {
  const idField = prefix === 'target_user'
    ? 'target_user_id'
    : `${prefix}_user_id`;
  return {
    id: readString(item?.[idField], item?.metadata?.[idField]),
    name: readString(
      item?.[`${prefix}_name`],
      item?.metadata?.[`${prefix}_name`]
    ),
    email: readString(
      item?.[`${prefix}_email`],
      item?.metadata?.[`${prefix}_email`]
    ),
  };
};

const getPersonLabel = (person, fallback = 'Requester unavailable') => (
  readString(person?.name, person?.email) || fallback
);

const isSamePerson = (person, user) => {
  const personId = readString(person?.id);
  const userId = readString(user?.id);
  if (personId && userId) return personId === userId;
  const personEmail = readString(person?.email).toLowerCase();
  const userEmail = readString(user?.email).toLowerCase();
  return Boolean(personEmail && userEmail && personEmail === userEmail);
};

const getRequestedBy = (item) => (
  getPersonLabel(getRequestPerson(item, 'requested_by'))
);

const getRequestRelationship = (item, currentUser, { pending = false } = {}) => {
  const kind = getRequestKind(item);
  const requester = getRequestPerson(item, 'requested_by');
  const reviewer = getRequestPerson(item, 'reviewed_by');
  const target = getRequestPerson(item, 'target_user');
  const requesterLabel = getPersonLabel(requester);
  const reviewerLabel = getPersonLabel(reviewer, '');
  const targetLabel = getPersonLabel(target, '');
  const isRequester = isSamePerson(requester, currentUser);
  const isReviewer = isSamePerson(reviewer, currentUser);
  const isTarget = isSamePerson(target, currentUser);
  const targetNote = kind === 'brd_share_request' && targetLabel
    ? `Access for ${targetLabel}`
    : '';

  if (pending && isRequester) {
    return {
      tone: 'outgoing',
      icon: Send,
      label: 'Sent to',
      person: 'Application approvers',
      note: targetNote || 'Requested by you',
    };
  }

  if (pending) {
    return {
      tone: 'incoming',
      icon: ArrowDownLeft,
      label: 'Received from',
      person: requesterLabel,
      note: targetNote || 'Needs your review',
    };
  }

  if (isRequester && isReviewer) {
    return {
      tone: 'outgoing',
      icon: Send,
      label: 'Completed by',
      person: 'You',
      note: targetNote || 'Requested and reviewed by you',
    };
  }

  if (isReviewer) {
    return {
      tone: 'incoming',
      icon: ArrowDownLeft,
      label: 'Received from',
      person: requesterLabel,
      note: targetNote ? `${targetNote} · Reviewed by you` : 'Reviewed by you',
    };
  }

  if (isRequester) {
    return {
      tone: 'outgoing',
      icon: Send,
      label: reviewerLabel ? 'Reviewed by' : 'Sent to',
      person: reviewerLabel || 'Application approvers',
      note: targetNote || 'Requested by you',
    };
  }

  if (isTarget) {
    return {
      tone: 'incoming',
      icon: ArrowDownLeft,
      label: 'Access request for you',
      person: requesterLabel,
      note: reviewerLabel ? `Reviewed by ${reviewerLabel}` : 'Application approval',
    };
  }

  return {
    tone: 'workspace',
    icon: UsersRound,
    label: 'Requested by',
    person: requesterLabel,
    note: reviewerLabel
      ? `Reviewed by ${reviewerLabel}`
      : targetNote || 'Application activity',
  };
};

const getRequestKey = (item, index = 0) => (
  [
    getRequestKind(item) || 'request',
    getPdfId(item) || 'pdf',
    getRequestId(item) || getSectionNumber(item) || item?.id || index,
    item?.status || 'status',
  ].join(':')
);

const getRequestContext = (item) => {
  const kind = getRequestKind(item);
  const pdfLabel = readString(
    item?.pdf_file,
    item?.metadata?.pdf_file,
    getPdfId(item)
  ) || 'BRD flow';
  const sectionTitle = readString(item?.section_title, item?.metadata?.section_title);
  const sectionNumber = getSectionNumber(item);

  if (kind === 'test_case_approval') {
    const sectionLabel = sectionTitle
      || (sectionNumber > 0 ? `Section ${sectionNumber}` : 'Saved test cases');
    return `${sectionLabel} · ${pdfLabel}`;
  }

  if (kind === 'brd_share_request') {
    const target = readString(
      item?.target_user_name,
      item?.target_user_email,
      item?.metadata?.target_user_name,
      item?.metadata?.target_user_email
    );
    return target ? `${pdfLabel} · Share with ${target}` : `${pdfLabel} · Access request`;
  }

  return pdfLabel;
};

const getRejectLabel = (item) => {
  const kind = getRequestKind(item);
  if (kind === 'test_case_approval') return 'Request changes';
  if (kind === 'brd_share_request') return 'Decline';
  return 'Reject';
};

const getRejectDialogCopy = (item) => {
  const kind = getRequestKind(item);
  if (kind === 'test_case_approval') {
    return {
      eyebrow: 'Request changes',
      title: 'Send this test-case review back?',
      copy: 'Explain what should change so the author can revise the saved test cases.',
      fieldLabel: 'Changes required',
      placeholder: 'Describe the missing coverage, incorrect expectation, or required update.',
      submitLabel: 'Request changes',
      reasonRequired: true,
    };
  }
  if (kind === 'brd_share_request') {
    return {
      eyebrow: 'Decline sharing',
      title: 'Decline this sharing request?',
      copy: 'The requester will not receive access to this BRD.',
      fieldLabel: 'Reason (optional)',
      placeholder: 'Add context for the requester, if helpful.',
      submitLabel: 'Decline request',
      reasonRequired: false,
    };
  }
  return {
    eyebrow: 'Reject BRD',
    title: 'Reject this BRD approval?',
    copy: 'Add a clear reason so the owner knows what must be corrected before resubmitting.',
    fieldLabel: 'Rejection reason',
    placeholder: 'Describe the issue that prevents approval.',
    submitLabel: 'Reject BRD',
    reasonRequired: true,
  };
};

const isPendingRequestItem = (item) => {
  const kind = getRequestKind(item);
  const status = String(item?.status || '').toLowerCase();
  if (kind === 'brd_approval') return status === 'pending_approval';
  if (kind === 'brd_share_request') return status === 'pending';
  if (kind === 'test_case_approval') return status === 'pending_review';
  return false;
};

const canTakeRequestAction = (item) => {
  if (!isPendingRequestItem(item) || !getPdfId(item)) return false;
  const kind = getRequestKind(item);
  if (kind === 'brd_approval') return true;
  if (kind === 'brd_share_request') return Boolean(getRequestId(item));
  if (kind === 'test_case_approval') return getSectionNumber(item) > 0;
  return false;
};

const getResolvedRequestStatus = (item, action) => {
  if (action === 'approve') return 'approved';
  return getRequestKind(item) === 'test_case_approval'
    ? 'changes_requested'
    : 'rejected';
};

const getSummaryKeyForKind = (kind) => {
  if (kind === 'brd_approval') return 'brd_pending';
  if (kind === 'brd_share_request') return 'sharing_pending';
  if (kind === 'test_case_approval') return 'test_case_pending';
  return '';
};

const actionKeyFor = (item, action) => (
  `${getRequestKind(item) || 'request'}:${getPdfId(item) || 'pdf'}:${
    getRequestId(item) || getSectionNumber(item) || 'root'
  }:${action}`
);

const parseApiError = async (response, fallbackMessage) => {
  const payload = await response.json().catch(() => ({}));
  return payload?.detail || fallbackMessage;
};

const normalizeTestCasePayload = (detailData) => {
  const latestArtifacts = detailData?.latestArtifacts || {};
  const active = latestArtifacts?.test_cases?.active || null;
  const draft = latestArtifacts?.test_cases?.draft || null;
  const source = draft || active;
  const payload = source?.payload?.test_cases || source?.payload || null;
  if (!payload) return { cases: [], total: 0, sourceLabel: 'No saved test cases' };

  const rows = [];
  if (Array.isArray(payload)) {
    payload.forEach((testCase) => rows.push({ category: null, testCase }));
  } else if (payload && typeof payload === 'object') {
    const positive = Array.isArray(payload.positive) ? payload.positive : [];
    const negative = Array.isArray(payload.negative) ? payload.negative : [];
    positive.forEach((testCase) => rows.push({ category: 'Positive', testCase }));
    negative.forEach((testCase) => rows.push({ category: 'Negative', testCase }));
    if (rows.length === 0 && !('positive' in payload) && !('negative' in payload)) {
      rows.push({ category: null, testCase: payload });
    }
  }

  return {
    cases: rows,
    total: rows.length,
    sourceLabel: draft ? 'Draft saved version' : 'Active saved version',
  };
};

const renderStepsPreview = (steps) => {
  if (Array.isArray(steps)) {
    return steps
      .slice(0, 3)
      .map((step) => {
        if (typeof step === 'string') return step.trim();
        if (!step || typeof step !== 'object') return String(step || '').trim();
        return readString(step.action, step.step, step.description, step.expected_result);
      })
      .filter(Boolean)
      .join(' · ');
  }
  return String(steps || '').trim();
};

const buildHistoryContext = (item) => {
  const pdfLabel = readString(
    item?.pdf_file,
    item?.metadata?.pdf_file,
    getPdfId(item)
  );
  const sectionTitle = readString(item?.section_title, item?.metadata?.section_title);
  const sectionNumber = getSectionNumber(item);

  if (sectionTitle) {
    if (sectionTitle.toLowerCase() === getRequestTitle(item).toLowerCase()) {
      return pdfLabel || sectionTitle;
    }
    return pdfLabel ? `${sectionTitle} · ${pdfLabel}` : sectionTitle;
  }
  if (sectionNumber > 0) {
    return pdfLabel ? `Section ${sectionNumber} · ${pdfLabel}` : `Section ${sectionNumber}`;
  }
  return pdfLabel || 'Application update';
};

const buildSearchText = (item) => (
  [
    getRequestTitle(item),
    getRequestContext(item),
    buildHistoryContext(item),
    getRequestedBy(item),
    getPersonLabel(getRequestPerson(item, 'reviewed_by'), ''),
    getPersonLabel(getRequestPerson(item, 'target_user'), ''),
    item?.reason,
    item?.status,
    toKindMeta(getRequestKind(item)).label,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
);

const trapDialogFocus = (event, container) => {
  if (event.key !== 'Tab' || !container) return;
  const focusable = Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR))
    .filter((element) => !element.hasAttribute('disabled'));
  if (!focusable.length) {
    event.preventDefault();
    container.focus();
    return;
  }

  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const RequestListSkeleton = ({ cards = 3 }) => (
  <div className="request-skeleton-list" aria-hidden="true">
    {Array.from({ length: cards }, (_, index) => (
      <div className="request-skeleton-card" key={`request-skeleton-${index}`}>
        <div className="request-skeleton-icon" />
        <div className="request-skeleton-copy">
          <div className="request-skeleton-line request-skeleton-line-short" />
          <div className="request-skeleton-line request-skeleton-line-title" />
          <div className="request-skeleton-line" />
          <div className="request-skeleton-line request-skeleton-line-meta" />
        </div>
      </div>
    ))}
  </div>
);

const RequestEmptyState = ({
  icon: Icon = CheckCircle2,
  tone = 'success',
  title,
  copy,
  children = null,
}) => (
  <div className={`request-empty-state request-empty-state-${tone}`}>
    <span className="request-empty-icon" aria-hidden="true">
      <Icon />
    </span>
    <h3>{title}</h3>
    <p>{copy}</p>
    {children && <div className="request-empty-actions">{children}</div>}
  </div>
);

const RequestRelationship = ({ relationship }) => {
  const DirectionIcon = relationship.icon;
  return (
    <div
      className={`request-relationship request-relationship-${relationship.tone}`}
      role="group"
      aria-label={`${relationship.label} ${relationship.person}. ${relationship.note}`}
    >
      <span className="request-relationship-icon" aria-hidden="true">
        <DirectionIcon />
      </span>
      <div>
        <span>{relationship.label}</span>
        <strong>{relationship.person}</strong>
        {relationship.note && <small>{relationship.note}</small>}
      </div>
    </div>
  );
};

export const RequestsCenterPage = ({
  currentUser = null,
  onBack = null,
  onRequestsChanged = null,
}) => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeView, setActiveView] = useState('pending');
  const [searchQuery, setSearchQuery] = useState('');
  const [historyLimit, setHistoryLimit] = useState(HISTORY_PAGE_SIZE);
  const [summary, setSummary] = useState(INITIAL_SUMMARY);
  const [summaryLoaded, setSummaryLoaded] = useState(false);
  const [items, setItems] = useState([]);
  const [historyItems, setHistoryItems] = useState([]);
  const [loadedFilter, setLoadedFilter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pageActionError, setPageActionError] = useState(null);
  const [actionKey, setActionKey] = useState('');
  const [actionMessage, setActionMessage] = useState(null);
  const [detailItem, setDetailItem] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailActionError, setDetailActionError] = useState(null);
  const [rejectionItem, setRejectionItem] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionError, setRejectionError] = useState(null);

  const requestLoadSequenceRef = useRef(0);
  const detailLoadSequenceRef = useRef(0);
  const detailOpenerRef = useRef(null);
  const detailCloseRef = useRef(null);
  const detailDialogRef = useRef(null);
  const rejectionOpenerRef = useRef(null);
  const rejectionReasonRef = useRef(null);
  const rejectionDialogRef = useRef(null);
  const queueHeadingRef = useRef(null);

  const isCurrentFilterLoaded = loadedFilter === activeFilter;
  const isInitialLoading = loading && !isCurrentFilterLoaded;
  const currentItems = useMemo(
    () => (isCurrentFilterLoaded ? items : []),
    [isCurrentFilterLoaded, items]
  );
  const currentHistoryItems = useMemo(
    () => (isCurrentFilterLoaded ? historyItems : []),
    [historyItems, isCurrentFilterLoaded]
  );

  const loadRequests = useCallback(async () => {
    const requestedFilter = activeFilter;
    const loadSequence = requestLoadSequenceRef.current + 1;
    requestLoadSequenceRef.current = loadSequence;
    setLoading(true);
    setError(null);

    try {
      const [summaryResponse, itemsResponse, historyResponse] = await Promise.all([
        apiFetch(API_ENDPOINTS.REQUESTS_SUMMARY),
        apiFetch(
          `${API_ENDPOINTS.REQUESTS_PENDING}?kind=${encodeURIComponent(requestedFilter)}&limit=100`
        ),
        apiFetch(
          `${API_ENDPOINTS.REQUESTS_HISTORY}?kind=${encodeURIComponent(requestedFilter)}&limit=100`
        ),
      ]);

      if (requestLoadSequenceRef.current !== loadSequence) return;

      if (summaryResponse.ok) {
        const payload = await summaryResponse.json().catch(() => ({}));
        const nextSummary = payload?.summary || {};
        setSummary({
          brd_pending: normalizeCount(nextSummary?.brd_pending),
          sharing_pending: normalizeCount(nextSummary?.sharing_pending),
          test_case_pending: normalizeCount(nextSummary?.test_case_pending),
          total_pending: normalizeCount(nextSummary?.total_pending),
        });
        setSummaryLoaded(true);
      } else {
        setSummaryLoaded(false);
      }

      if (!itemsResponse.ok) {
        throw new Error(
          await parseApiError(itemsResponse, 'Failed to load pending requests.')
        );
      }
      if (!historyResponse.ok) {
        throw new Error(
          await parseApiError(historyResponse, 'Failed to load request history.')
        );
      }

      const itemsPayload = await itemsResponse.json().catch(() => ({}));
      const historyPayload = await historyResponse.json().catch(() => ({}));
      if (requestLoadSequenceRef.current !== loadSequence) return;

      setItems(Array.isArray(itemsPayload?.items) ? itemsPayload.items : []);
      setHistoryItems(Array.isArray(historyPayload?.items) ? historyPayload.items : []);
      setLoadedFilter(requestedFilter);
      return true;
    } catch (err) {
      if (requestLoadSequenceRef.current !== loadSequence) return;
      setError(err?.message || 'Failed to load Request Center.');
      return false;
    } finally {
      if (requestLoadSequenceRef.current === loadSequence) {
        setLoading(false);
      }
    }
  }, [activeFilter]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  useEffect(() => {
    setHistoryLimit(HISTORY_PAGE_SIZE);
  }, [activeFilter, searchQuery]);

  const refreshEverything = useCallback(async () => {
    await loadRequests();
    if (typeof onRequestsChanged === 'function') {
      try {
        await onRequestsChanged();
      } catch (_error) {
        // Header notification refresh is best-effort and should not hide a healthy queue.
      }
    }
  }, [loadRequests, onRequestsChanged]);

  const closeRequestCenter = () => {
    if (typeof onBack === 'function') {
      onBack();
      return;
    }
    navigate(buildApplicationsPath());
  };

  const openRequest = (item) => {
    const explicitPath = readString(item?.link?.path);
    if (explicitPath) {
      navigate(explicitPath.startsWith('/') ? explicitPath : `/${explicitPath}`);
      return;
    }

    const pdfId = getPdfId(item);
    if (!pdfId) return;
    const kind = getRequestKind(item);
    const sectionNumber = getSectionNumber(item);
    const query = new URLSearchParams({ pdf_id: pdfId });

    if (kind === 'test_case_approval' && sectionNumber > 0) {
      query.set('section', String(sectionNumber));
      query.set('step', '6');
    } else {
      query.set('step', '3');
    }
    navigate(`/pdf?${query.toString()}`);
  };

  const openBrdSource = (pdfId) => {
    const normalizedPdfId = String(pdfId || '').trim();
    if (!normalizedPdfId) return;
    const url = `${API_BASE_URL}${API_ENDPOINTS.PDF_SOURCE_FILE(normalizedPdfId)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const closeDetails = useCallback((restoreFocus = true) => {
    detailLoadSequenceRef.current += 1;
    setDetailItem(null);
    setDetailData(null);
    setDetailError(null);
    setDetailActionError(null);
    setDetailLoading(false);
    if (restoreFocus) {
      window.requestAnimationFrame(() => detailOpenerRef.current?.focus?.());
    }
  }, []);

  const closeRejectionDialog = useCallback((restoreFocus = true) => {
    setRejectionItem(null);
    setRejectionReason('');
    setRejectionError(null);
    if (restoreFocus) {
      window.requestAnimationFrame(() => rejectionOpenerRef.current?.focus?.());
    }
  }, []);

  useEffect(() => {
    if (!detailItem && !rejectionItem) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key !== 'Escape') return;
      if (actionKey) {
        event.preventDefault();
        return;
      }
      if (rejectionItem) {
        closeRejectionDialog();
      } else if (detailItem) {
        closeDetails();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = previousOverflow;
    };
  }, [
    closeDetails,
    closeRejectionDialog,
    detailItem,
    rejectionItem,
    actionKey,
  ]);

  useEffect(() => {
    if (!detailItem) return undefined;
    const animationFrame = window.requestAnimationFrame(() => detailCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(animationFrame);
  }, [detailItem]);

  useEffect(() => {
    if (!rejectionItem) return undefined;
    const animationFrame = window.requestAnimationFrame(() => rejectionReasonRef.current?.focus());
    return () => window.cancelAnimationFrame(animationFrame);
  }, [rejectionItem]);

  const openDetails = async (item) => {
    const pdfId = getPdfId(item);
    if (!pdfId) return;
    const kind = getRequestKind(item);
    const sectionNumber = getSectionNumber(item);
    const loadSequence = detailLoadSequenceRef.current + 1;
    detailLoadSequenceRef.current = loadSequence;
    detailOpenerRef.current = document.activeElement;

    setDetailItem(item);
    setDetailData(null);
    setDetailError(null);
    setDetailActionError(null);
    setDetailLoading(true);

    try {
      const requests = [];
      if (kind === 'test_case_approval' && sectionNumber > 0) {
        requests.push(
          apiFetch(API_ENDPOINTS.SECTION_LATEST_ARTIFACTS(pdfId, sectionNumber)),
          apiFetch(API_ENDPOINTS.TEST_CASE_APPROVAL_STATUS(pdfId, sectionNumber)),
          apiFetch(API_ENDPOINTS.TEST_CASE_APPROVAL_EVENTS(pdfId, sectionNumber))
        );
      } else if (kind === 'brd_approval') {
        requests.push(
          apiFetch(API_ENDPOINTS.PDF_APPROVAL_STATUS(pdfId)),
          apiFetch(API_ENDPOINTS.PDF_APPROVAL_EVENTS(pdfId))
        );
      } else if (kind === 'brd_share_request') {
        requests.push(apiFetch(API_ENDPOINTS.BRD_SHARING_REQUESTS(pdfId)));
      }

      const results = await Promise.all(requests);
      if (detailLoadSequenceRef.current !== loadSequence) return;

      const requiredResultIndexes = kind === 'test_case_approval' ? [0, 1] : [0];
      for (const resultIndex of requiredResultIndexes) {
        const response = results[resultIndex];
        if (response && !response.ok) {
          throw new Error(
            await parseApiError(response, 'Failed to load request details.')
          );
        }
      }

      const payloads = await Promise.all(
        results.map(async (response) => {
          if (!response?.ok) return {};
          return response.json().catch(() => ({}));
        })
      );
      if (detailLoadSequenceRef.current !== loadSequence) return;

      if (kind === 'test_case_approval') {
        setDetailData({
          latestArtifacts: payloads[0] || {},
          approval: payloads[1]?.approval || null,
          events: Array.isArray(payloads[2]?.events) ? payloads[2].events : [],
        });
      } else if (kind === 'brd_approval') {
        setDetailData({
          approval: payloads[0]?.approval || null,
          events: Array.isArray(payloads[1]?.events) ? payloads[1].events : [],
        });
      } else {
        setDetailData({
          sharingRequests: Array.isArray(payloads[0]?.requests)
            ? payloads[0].requests
            : [],
        });
      }
    } catch (err) {
      if (detailLoadSequenceRef.current !== loadSequence) return;
      setDetailError(err?.message || 'Failed to load request details.');
    } finally {
      if (detailLoadSequenceRef.current === loadSequence) {
        setDetailLoading(false);
      }
    }
  };

  const runRequestAction = async (
    item,
    action,
    reason = '',
    errorTarget = 'page'
  ) => {
    const pdfId = getPdfId(item);
    if (!pdfId || actionKey) return false;

    const kind = getRequestKind(item);
    const sectionNumber = getSectionNumber(item);
    const requestId = getRequestId(item);
    const normalizedReason = String(reason || '').trim();
    let endpoint = '';

    if (
      action === 'reject'
      && kind !== 'brd_share_request'
      && !normalizedReason
    ) {
      setRejectionError('Please add a reason before continuing.');
      return false;
    }

    if (kind === 'brd_approval') {
      endpoint = action === 'approve'
        ? API_ENDPOINTS.PDF_APPROVAL_APPROVE(pdfId)
        : API_ENDPOINTS.PDF_APPROVAL_REJECT(pdfId);
    } else if (kind === 'brd_share_request') {
      if (!requestId) return false;
      endpoint = action === 'approve'
        ? API_ENDPOINTS.BRD_SHARING_APPROVE(pdfId, requestId)
        : API_ENDPOINTS.BRD_SHARING_REJECT(pdfId, requestId);
    } else if (kind === 'test_case_approval') {
      if (sectionNumber <= 0) return false;
      endpoint = action === 'approve'
        ? API_ENDPOINTS.TEST_CASE_APPROVAL_APPROVE(pdfId, sectionNumber)
        : API_ENDPOINTS.TEST_CASE_APPROVAL_REJECT(pdfId, sectionNumber);
    }

    if (!endpoint) return false;

    const nextActionKey = actionKeyFor(item, action);
    setActionKey(nextActionKey);
    setActionMessage(null);
    setError(null);
    setPageActionError(null);
    setDetailActionError(null);
    setRejectionError(null);

    try {
      const response = await apiFetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(normalizedReason ? { reason: normalizedReason } : {}),
      });
      if (!response.ok) {
        throw new Error(
          await parseApiError(response, `Failed to ${action} request.`)
        );
      }

      const resolvedStatus = getResolvedRequestStatus(item, action);
      const resolvedAt = new Date().toISOString();
      const targetKey = actionKeyFor(item, 'identity');
      const isTargetRequest = (candidate) => (
        actionKeyFor(candidate, 'identity') === targetKey
      );
      const resolvedItem = {
        ...item,
        status: resolvedStatus,
        updated_at: resolvedAt,
        reviewed_at: resolvedAt,
        reviewed_by_user_id: currentUser?.id || null,
        reviewed_by_name: currentUser?.name || null,
        reviewed_by_email: currentUser?.email || null,
      };

      setItems((current) => current.filter((candidate) => !isTargetRequest(candidate)));
      setHistoryItems((current) => [
        resolvedItem,
        ...current.filter((candidate) => !isTargetRequest(candidate)),
      ]);
      if (summaryLoaded) {
        const summaryKey = getSummaryKeyForKind(kind);
        setSummary((current) => ({
          ...current,
          total_pending: Math.max(0, current.total_pending - 1),
          ...(summaryKey
            ? { [summaryKey]: Math.max(0, current[summaryKey] - 1) }
            : {}),
        }));
      }

      closeRejectionDialog(false);
      closeDetails(false);
      setActionMessage(
        action === 'approve'
          ? `${getRequestTitle(item)} was approved.`
          : `${getRequestTitle(item)} was sent back.`
      );
      window.requestAnimationFrame(() => queueHeadingRef.current?.focus());

      await loadRequests();
      setItems((current) => current.filter((candidate) => !isTargetRequest(candidate)));
      setHistoryItems((current) => {
        const resolvedTarget = current.find(
          (candidate) => isTargetRequest(candidate) && !isPendingRequestItem(candidate)
        );
        if (resolvedTarget) return current;
        return [
          resolvedItem,
          ...current.filter((candidate) => !isTargetRequest(candidate)),
        ];
      });
      if (typeof onRequestsChanged === 'function') {
        try {
          await onRequestsChanged();
        } catch (_error) {
          // A successful decision should remain successful if the header refresh fails.
        }
      }

      return true;
    } catch (err) {
      const message = err?.message || `Failed to ${action} request.`;
      if (errorTarget === 'dialog') {
        setRejectionError(message);
      } else if (errorTarget === 'drawer') {
        setDetailActionError(message);
      } else {
        setPageActionError(message);
      }
      return false;
    } finally {
      setActionKey('');
    }
  };

  const requestAction = (item, action, errorTarget = 'page') => {
    if (action === 'reject') {
      rejectionOpenerRef.current = document.activeElement;
      setRejectionItem(item);
      setRejectionReason('');
      setRejectionError(null);
      return;
    }
    runRequestAction(item, action, '', errorTarget);
  };

  const filteredPendingItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return currentItems;
    return currentItems.filter((item) => buildSearchText(item).includes(query));
  }, [currentItems, searchQuery]);

  const filteredHistoryItems = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return currentHistoryItems;
    return currentHistoryItems.filter((item) => buildSearchText(item).includes(query));
  }, [currentHistoryItems, searchQuery]);

  const visibleHistoryItems = filteredHistoryItems.slice(0, historyLimit);
  const hiddenHistoryCount = Math.max(
    0,
    filteredHistoryItems.length - visibleHistoryItems.length
  );

  const headline = useMemo(() => {
    if (activeFilter === 'brd') return 'BRD approval requests';
    if (activeFilter === 'sharing') return 'BRD sharing requests';
    if (activeFilter === 'testcases') return 'Test-case approval requests';
    return 'All approval requests';
  }, [activeFilter]);

  const stageStatus = useMemo(() => {
    if (isInitialLoading) {
      return {
        icon: Loader2,
        label: 'Loading queue',
        loading: true,
      };
    }
    if (loading) {
      return {
        icon: RefreshCw,
        label: 'Refreshing queue',
        loading: true,
      };
    }
    if (error && !isCurrentFilterLoaded) {
      return {
        icon: AlertCircle,
        label: 'Needs attention',
        tone: 'danger',
      };
    }
    if (!summaryLoaded && isCurrentFilterLoaded) {
      return {
        icon: Inbox,
        label: `${currentItems.length} shown`,
      };
    }
    if (summaryLoaded && summary.total_pending === 0) {
      return {
        icon: CheckCircle2,
        label: 'All caught up',
        tone: 'success',
      };
    }
    return {
      icon: Bell,
      label: `${summary.total_pending} pending`,
    };
  }, [
    error,
    currentItems.length,
    isCurrentFilterLoaded,
    isInitialLoading,
    loading,
    summary.total_pending,
    summaryLoaded,
  ]);

  const sidebarMetrics = [
    {
      label: 'Total pending',
      value: summaryLoaded ? summary.total_pending : '—',
    },
    {
      label: 'BRD approvals',
      value: summaryLoaded ? summary.brd_pending : '—',
    },
    {
      label: 'Sharing requests',
      value: summaryLoaded ? summary.sharing_pending : '—',
    },
    {
      label: 'Test-case reviews',
      value: summaryLoaded ? summary.test_case_pending : '—',
    },
  ];

  const handleViewKeyDown = (event, viewIndex) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
    let nextIndex = viewIndex;
    if (event.key === 'ArrowRight') {
      nextIndex = (viewIndex + 1) % REQUEST_VIEWS.length;
    } else if (event.key === 'ArrowLeft') {
      nextIndex = (viewIndex - 1 + REQUEST_VIEWS.length) % REQUEST_VIEWS.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = REQUEST_VIEWS.length - 1;
    }
    const nextView = REQUEST_VIEWS[nextIndex];
    setActiveView(nextView.id);
    document.getElementById(`request-view-${nextView.id}`)?.focus();
  };

  const renderActionButtons = (item, { drawer = false } = {}) => {
    if (!canTakeRequestAction(item)) return null;
    const title = getRequestTitle(item);
    const approveKey = actionKeyFor(item, 'approve');
    const rejectKey = actionKeyFor(item, 'reject');
    const isApproving = actionKey === approveKey;
    const isRejecting = actionKey === rejectKey;
    const anyActionBusy = Boolean(actionKey);

    return (
      <div className={joinClasses('request-decision-actions', drawer && 'is-drawer')}>
        <button
          type="button"
          className="request-action request-action-approve"
          onClick={() => requestAction(item, 'approve', drawer ? 'drawer' : 'page')}
          disabled={anyActionBusy}
          aria-label={`Approve ${title}`}
          aria-busy={isApproving}
        >
          {isApproving
            ? <Loader2 className="animate-spin" aria-hidden="true" />
            : <CheckCircle2 aria-hidden="true" />}
          {isApproving ? 'Approving…' : 'Approve'}
        </button>
        <button
          type="button"
          className="request-action request-action-reject"
          onClick={() => requestAction(item, 'reject')}
          disabled={anyActionBusy}
          aria-label={`${getRejectLabel(item)} ${title}`}
          aria-busy={isRejecting}
        >
          {isRejecting
            ? <Loader2 className="animate-spin" aria-hidden="true" />
            : <XCircle aria-hidden="true" />}
          {isRejecting ? 'Working…' : getRejectLabel(item)}
        </button>
      </div>
    );
  };

  const renderPendingCard = (item, index) => {
    const kind = getRequestKind(item);
    const kindMeta = toKindMeta(kind);
    const statusMeta = toStatusMeta(item?.status);
    const KindIcon = kindMeta.icon;
    const title = getRequestTitle(item);
    const context = getRequestContext(item);
    const relationship = getRequestRelationship(item, currentUser, { pending: true });
    const updatedAt = item?.updated_at || item?.requested_at || null;
    const updatedLabel = formatShortDate(updatedAt);
    const cardKey = getRequestKey(item, index);
    const cardTitleId = `pending-request-title-${index}`;
    const cardBusy = actionKey.startsWith(
      `${kind || 'request'}:${getPdfId(item) || 'pdf'}:${
        getRequestId(item) || getSectionNumber(item) || 'root'
      }:`
    );
    const canOpen = Boolean(getPdfId(item));

    return (
      <li key={cardKey}>
        <article
          className={`request-card ${kindMeta.cardClass}`}
          aria-labelledby={cardTitleId}
          aria-busy={cardBusy}
        >
          <header className="request-tile-header">
            <div className="request-tile-kind">
              <span className="request-card-icon" aria-hidden="true">
                <KindIcon />
              </span>
              <div>
                <span className="request-tile-kind-label">
                  {kindMeta.tileLabel}
                </span>
                {updatedLabel && (
                  <time
                    dateTime={toDateTimeAttribute(updatedAt)}
                    title={formatDateTime(updatedAt)}
                  >
                    Updated {updatedLabel}
                  </time>
                )}
              </div>
            </div>

            <div className="request-tile-controls">
              <span className={`request-status-badge ${statusMeta.badgeClass}`}>
                {statusMeta.label}
              </span>
              <button
                type="button"
                className="request-tile-open"
                onClick={() => openRequest(item)}
                disabled={!canOpen || Boolean(actionKey)}
                aria-label={`Open application flow for ${title}`}
                title="Open application flow"
              >
                <ArrowUpRight aria-hidden="true" />
              </button>
            </div>
          </header>

          <div className="request-tile-body">
            <h3 id={cardTitleId}>{title}</h3>
            <p className="request-card-context">{context}</p>

            <RequestRelationship relationship={relationship} />

            {getSectionNumber(item) > 0 && (
              <div className="request-card-meta">
                <span>Section {getSectionNumber(item)}</span>
              </div>
            )}
          </div>

          <footer className="request-card-footer">
            <button
              type="button"
              className="request-action request-action-review"
              onClick={() => openDetails(item)}
              disabled={!canOpen || Boolean(actionKey)}
              aria-label={`Review ${title}`}
            >
              <Eye aria-hidden="true" />
              Review
            </button>

            {renderActionButtons(item)}
          </footer>
        </article>
      </li>
    );
  };

  const renderHistoryCard = (item, index) => {
    const kind = getRequestKind(item);
    const status = String(item?.status || '').toLowerCase();
    const canContinue = kind === 'test_case_approval' && status === 'approved';
    const kindMeta = toKindMeta(kind);
    const statusMeta = toStatusMeta(status);
    const KindIcon = kindMeta.icon;
    const title = getRequestTitle(item);
    const updatedAt = item?.reviewed_at || item?.updated_at || null;
    const updatedLabel = formatShortDate(updatedAt);
    const canOpen = Boolean(getPdfId(item));
    const titleId = `history-request-title-${index}`;
    const relationship = getRequestRelationship(item, currentUser);

    return (
      <li key={getRequestKey(item, index)}>
        <article
          className={`request-history-card ${kindMeta.cardClass}`}
          aria-labelledby={titleId}
        >
          <header className="request-tile-header">
            <div className="request-tile-kind">
              <span className="request-card-icon" aria-hidden="true">
                <KindIcon />
              </span>
              <div>
                <span className="request-tile-kind-label">
                  {kindMeta.tileLabel}
                </span>
                {updatedLabel && (
                  <time
                    dateTime={toDateTimeAttribute(updatedAt)}
                    title={formatDateTime(updatedAt)}
                  >
                    Resolved {updatedLabel}
                  </time>
                )}
              </div>
            </div>

            <div className="request-tile-controls">
              <span className={`request-status-badge ${statusMeta.badgeClass}`}>
                {statusMeta.label}
              </span>
            </div>
          </header>

          <div className="request-tile-body">
            <h3 id={titleId}>{title}</h3>
            <p className="request-history-context">{buildHistoryContext(item)}</p>

            <RequestRelationship relationship={relationship} />

            {item?.reason && <p className="request-history-reason">{item.reason}</p>}
          </div>

          <footer className="request-history-footer">
            <div>
              <button
                type="button"
                className="request-action request-action-subtle"
                onClick={() => openDetails(item)}
                disabled={!canOpen}
                aria-label={`Review history for ${title}`}
              >
                <Eye aria-hidden="true" />
                Review
              </button>
              <button
                type="button"
                className={joinClasses(
                  'request-action',
                  canContinue
                    ? 'request-action-review'
                    : 'request-action-subtle'
                )}
                onClick={() => openRequest(item)}
                disabled={!canOpen}
                aria-label={`${canContinue ? 'Continue' : 'Open'} ${title}`}
              >
                <ArrowUpRight aria-hidden="true" />
                {canContinue ? 'Continue' : 'Open'}
              </button>
            </div>
          </footer>
        </article>
      </li>
    );
  };

  const renderToolbar = () => (
    <section className="request-toolbar" aria-label="Request Center controls">
      <div className="request-toolbar-primary">
        <div
          className="request-view-tabs"
          role="tablist"
          aria-label="Request queue view"
        >
          {REQUEST_VIEWS.map((view, index) => {
            const ViewIcon = view.icon;
            const count = view.id === 'pending'
              ? summaryLoaded
                ? summary.total_pending
                : currentItems.length
              : currentHistoryItems.length;
            const isActive = activeView === view.id;
            return (
              <button
                key={view.id}
                id={`request-view-${view.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                aria-controls={`request-panel-${view.id}`}
                tabIndex={isActive ? 0 : -1}
                className={isActive ? 'is-active' : ''}
                onClick={() => setActiveView(view.id)}
                onKeyDown={(event) => handleViewKeyDown(event, index)}
                disabled={Boolean(actionKey)}
              >
                <ViewIcon aria-hidden="true" />
                <span>{view.label}</span>
                <strong>{count}</strong>
              </button>
            );
          })}
        </div>

        <label className="request-search">
          <Search aria-hidden="true" />
          <span className="sr-only">Search requests</span>
          <input
            type="search"
            value={searchQuery}
            onChange={(event) => setSearchQuery(event.target.value)}
            placeholder="Search title, document, or requester"
            disabled={Boolean(actionKey)}
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              aria-label="Clear request search"
            >
              <X aria-hidden="true" />
            </button>
          )}
        </label>
      </div>

      <div
        className="request-kind-filters"
        role="group"
        aria-label="Filter requests by type"
      >
        {REQUEST_FILTERS.map((filter) => {
          const isActive = activeFilter === filter.id;
          const count = summaryLoaded
            ? normalizeCount(summary?.[filter.summaryKey])
            : isActive && isCurrentFilterLoaded
              ? currentItems.length
              : '—';
          return (
            <button
              key={filter.id}
              type="button"
              aria-pressed={isActive}
              aria-controls={`request-panel-${activeView}`}
              className={isActive ? 'is-active' : ''}
              disabled={Boolean(actionKey)}
              onClick={() => {
                if (filter.id === activeFilter) return;
                closeDetails(false);
                setActiveFilter(filter.id);
              }}
            >
              <span>{filter.label}</span>
              {activeView === 'pending' && <strong>{count}</strong>}
            </button>
          );
        })}

        <span className="request-result-summary" aria-live="polite">
          {activeView === 'pending'
            ? `${filteredPendingItems.length} pending request${
              filteredPendingItems.length === 1 ? '' : 's'
            } shown`
            : `${filteredHistoryItems.length} resolved request${
              filteredHistoryItems.length === 1 ? '' : 's'
            } shown`}
        </span>
      </div>
    </section>
  );

  const renderPendingPanel = () => {
    const hasData = isCurrentFilterLoaded;
    const hasSearch = Boolean(searchQuery.trim());

    return (
      <section
        id="request-panel-pending"
        role="tabpanel"
        aria-labelledby="request-view-pending"
        aria-busy={loading}
        hidden={activeView !== 'pending'}
        className="request-results-panel"
      >
        <h3 ref={queueHeadingRef} className="sr-only" tabIndex={-1}>
          {headline}
        </h3>

        {isInitialLoading && (
          <>
            <span className="sr-only" role="status">Loading pending requests</span>
            <RequestListSkeleton />
          </>
        )}

        {!isInitialLoading && error && !hasData && (
          <RequestEmptyState
            icon={AlertCircle}
            tone="danger"
            title="We couldn’t load Request Center"
            copy={error}
          >
            <button
              type="button"
              className="request-empty-button"
              onClick={refreshEverything}
              disabled={loading}
            >
              <RefreshCw aria-hidden="true" />
              Try again
            </button>
          </RequestEmptyState>
        )}

        {!isInitialLoading && hasData && filteredPendingItems.length === 0 && (
          <RequestEmptyState
            icon={hasSearch ? Search : CheckCircle2}
            tone={hasSearch ? 'neutral' : 'success'}
            title={hasSearch ? 'No matching pending requests' : 'You’re all caught up'}
            copy={
              hasSearch
                ? 'Try a different title, document, or requester.'
                : activeFilter === 'all'
                  ? 'There are no requests waiting for review.'
                  : `There are no ${headline.toLowerCase()} waiting for review.`
            }
          >
            {hasSearch && (
              <button
                type="button"
                className="request-empty-button"
                onClick={() => setSearchQuery('')}
              >
                <X aria-hidden="true" />
                Clear search
              </button>
            )}
            {currentHistoryItems.length > 0 && (
              <button
                type="button"
                className="request-empty-button request-empty-button-secondary"
                onClick={() => setActiveView('history')}
              >
                <Clock3 aria-hidden="true" />
                View history
              </button>
            )}
          </RequestEmptyState>
        )}

        {!isInitialLoading && hasData && filteredPendingItems.length > 0 && (
          <ul className="request-card-list">
            {filteredPendingItems.map(renderPendingCard)}
          </ul>
        )}
      </section>
    );
  };

  const renderHistoryPanel = () => {
    const hasData = isCurrentFilterLoaded;
    const hasSearch = Boolean(searchQuery.trim());

    return (
      <section
        id="request-panel-history"
        role="tabpanel"
        aria-labelledby="request-view-history"
        aria-busy={loading}
        hidden={activeView !== 'history'}
        className="request-results-panel"
      >
        {isInitialLoading && (
          <>
            <span className="sr-only" role="status">Loading request history</span>
            <RequestListSkeleton cards={4} />
          </>
        )}

        {!isInitialLoading && error && !hasData && (
          <RequestEmptyState
            icon={AlertCircle}
            tone="danger"
            title="We couldn’t load request history"
            copy={error}
          >
            <button
              type="button"
              className="request-empty-button"
              onClick={refreshEverything}
              disabled={loading}
            >
              <RefreshCw aria-hidden="true" />
              Try again
            </button>
          </RequestEmptyState>
        )}

        {!isInitialLoading && hasData && filteredHistoryItems.length === 0 && (
          <RequestEmptyState
            icon={hasSearch ? Search : Clock3}
            tone="neutral"
            title={hasSearch ? 'No matching history' : 'No resolved requests yet'}
            copy={
              hasSearch
                ? 'Try a different title, document, or requester.'
                : 'Completed decisions will appear here with their review trail.'
            }
          >
            {hasSearch && (
              <button
                type="button"
                className="request-empty-button"
                onClick={() => setSearchQuery('')}
              >
                <X aria-hidden="true" />
                Clear search
              </button>
            )}
            <button
              type="button"
              className="request-empty-button request-empty-button-secondary"
              onClick={() => setActiveView('pending')}
            >
              <Inbox aria-hidden="true" />
              View pending
            </button>
          </RequestEmptyState>
        )}

        {!isInitialLoading && hasData && filteredHistoryItems.length > 0 && (
          <>
            <ul className="request-history-grid">
              {visibleHistoryItems.map(renderHistoryCard)}
            </ul>
            {hiddenHistoryCount > 0 && (
              <div className="request-show-more">
                <button
                  type="button"
                  onClick={() => setHistoryLimit((current) => current + HISTORY_PAGE_SIZE)}
                >
                  Show {Math.min(HISTORY_PAGE_SIZE, hiddenHistoryCount)} more
                  <span>{hiddenHistoryCount} remaining</span>
                </button>
              </div>
            )}
          </>
        )}
      </section>
    );
  };

  const renderDetailsDrawer = () => {
    if (!detailItem) return null;

    const kind = getRequestKind(detailItem);
    const kindMeta = toKindMeta(kind);
    const KindIcon = kindMeta.icon;
    const pdfId = getPdfId(detailItem);
    const sourceUrl = pdfId
      ? `${API_BASE_URL}${API_ENDPOINTS.PDF_SOURCE_FILE(pdfId)}`
      : '';
    const testCaseSummary = normalizeTestCasePayload(detailData);
    const sharingRequest = Array.isArray(detailData?.sharingRequests)
      ? detailData.sharingRequests.find(
        (request) => getRequestId(request) === getRequestId(detailItem)
      )
      : null;
    const detailStatus = kind === 'brd_share_request'
      ? sharingRequest?.status
      : readString(
        detailData?.approval?.approval_state,
        detailData?.approval?.status
      );
    const verifiedStatus = detailData !== null
      ? readString(detailStatus, 'resolved')
      : readString(detailItem?.status);
    const verifiedItem = verifiedStatus
      ? { ...detailItem, status: verifiedStatus }
      : detailItem;
    const relationshipItem = kind === 'brd_share_request' && sharingRequest
      ? { ...detailItem, ...sharingRequest, status: verifiedStatus }
      : verifiedItem;
    const relationship = getRequestRelationship(relationshipItem, currentUser, {
      pending: isPendingRequestItem(relationshipItem),
    });
    const isAwaitingDecision = isPendingRequestItem(verifiedItem);
    const hasActionContext = canTakeRequestAction(verifiedItem);
    const canTakeAction = (
      !detailLoading
      && !detailError
      && hasActionContext
    );
    const drawerTitleId = 'request-detail-title';
    const drawerDescriptionId = 'request-detail-description';

    return (
      <div
        className="request-drawer-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !actionKey) closeDetails();
        }}
      >
        <aside
          ref={detailDialogRef}
          className="request-drawer"
          role="dialog"
          aria-modal="true"
          aria-labelledby={drawerTitleId}
          aria-describedby={drawerDescriptionId}
          tabIndex={-1}
          onKeyDown={(event) => trapDialogFocus(event, detailDialogRef.current)}
        >
          <header className="request-drawer-header">
            <div className="request-drawer-heading">
              <span className={`request-drawer-kind-icon ${kindMeta.badgeClass}`} aria-hidden="true">
                <KindIcon />
              </span>
              <div>
                <p>{kindMeta.label}</p>
                <h2 id={drawerTitleId}>{getRequestTitle(detailItem)}</h2>
                <span id={drawerDescriptionId}>
                  Updated {formatDateTime(detailItem?.updated_at || detailItem?.requested_at)}
                </span>
              </div>
            </div>
            <button
              ref={detailCloseRef}
              type="button"
              className="request-drawer-close"
              onClick={() => closeDetails()}
              aria-label="Close request details"
              disabled={Boolean(actionKey)}
            >
              <X aria-hidden="true" />
            </button>
          </header>

          <div className="request-drawer-body">
            {detailLoading && (
              <div className="request-detail-notice" role="status">
                <Loader2 className="animate-spin" aria-hidden="true" />
                <div>
                  <strong>Loading request details</strong>
                  <span>Preparing the artifact and latest approval state.</span>
                </div>
              </div>
            )}

            {detailError && (
              <div className="request-detail-notice request-detail-notice-danger" role="alert">
                <AlertCircle aria-hidden="true" />
                <div>
                  <strong>Details could not be loaded</strong>
                  <span>{detailError}</span>
                </div>
              </div>
            )}

            {!detailLoading && !detailError && (
              <div className="request-detail-stack">
                <section className="request-detail-card request-detail-context">
                  <div className="request-detail-section-heading">
                    <p>Request context</p>
                    <span className={`request-status-badge ${toStatusMeta(verifiedStatus).badgeClass}`}>
                      {toStatusMeta(verifiedStatus).label}
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>Document</dt>
                      <dd>{readString(detailItem?.pdf_file, detailItem?.metadata?.pdf_file, pdfId)}</dd>
                    </div>
                    <div>
                      <dt>{relationship.label}</dt>
                      <dd>{relationship.person}</dd>
                    </div>
                    {kind === 'test_case_approval' && (
                      <div>
                        <dt>Section</dt>
                        <dd>
                          {readString(
                            detailItem?.section_title,
                            detailItem?.metadata?.section_title,
                            getSectionNumber(detailItem) > 0
                              ? `Section ${getSectionNumber(detailItem)}`
                              : 'Section'
                          )}
                        </dd>
                      </div>
                    )}
                    {kind === 'brd_share_request' && (
                      <div>
                        <dt>Share target</dt>
                        <dd>
                          {readString(
                            detailItem?.target_user_name,
                            detailItem?.target_user_email,
                            detailItem?.metadata?.target_user_name,
                            detailItem?.metadata?.target_user_email
                          ) || 'Application user'}
                        </dd>
                      </div>
                    )}
                  </dl>
                </section>

                {kind === 'test_case_approval' && (
                  <section className="request-detail-card">
                    <div className="request-detail-section-heading">
                      <div>
                        <p>Saved test cases</p>
                        <span>
                          {testCaseSummary.total} case{
                            testCaseSummary.total === 1 ? '' : 's'
                          } · {testCaseSummary.sourceLabel}
                        </span>
                      </div>
                      <button
                        type="button"
                        className="request-action request-action-subtle"
                        onClick={() => openRequest(detailItem)}
                      >
                        <ShieldCheck aria-hidden="true" />
                        Open application flow
                      </button>
                    </div>

                    <ol className="request-test-case-list">
                      {testCaseSummary.cases.slice(0, 8).map(
                        ({ category, testCase }, index) => (
                          <li key={`${testCase?.test_case_no || 'case'}-${index}`}>
                            <div>
                              <strong>
                                {testCase?.test_case_no || `Case ${index + 1}`}
                              </strong>
                              {category && <span>{category}</span>}
                            </div>
                            <p>
                              {testCase?.test_description
                                || testCase?.scenario
                                || testCase?.title
                                || 'No description'}
                            </p>
                            {renderStepsPreview(testCase?.test_steps) && (
                              <small>{renderStepsPreview(testCase.test_steps)}</small>
                            )}
                          </li>
                        )
                      )}
                    </ol>

                    {testCaseSummary.total > 8 && (
                      <p className="request-detail-footnote">
                        Showing 8 of {testCaseSummary.total} saved test cases.
                      </p>
                    )}
                    {testCaseSummary.total === 0 && (
                      <div className="request-detail-empty">
                        No saved test-case payload was found for this request.
                      </div>
                    )}
                  </section>
                )}

                {kind !== 'test_case_approval' && sourceUrl && (
                  <section className="request-detail-card">
                    <div className="request-detail-section-heading">
                      <div>
                        <p>BRD preview</p>
                        <span>Review the source before making a decision.</span>
                      </div>
                      <button
                        type="button"
                        className="request-action request-action-subtle"
                        onClick={() => openBrdSource(pdfId)}
                      >
                        <Eye aria-hidden="true" />
                        Open in new tab
                      </button>
                    </div>
                    <iframe
                      title="BRD preview"
                      src={sourceUrl}
                      className="request-brd-preview"
                    />
                    <p className="request-brd-mobile-note">
                      Open the BRD in a new tab for the best mobile review experience.
                    </p>
                  </section>
                )}

                {Array.isArray(detailData?.events) && detailData.events.length > 0 && (
                  <section className="request-detail-card">
                    <div className="request-detail-section-heading">
                      <p>Recent activity</p>
                    </div>
                    <ol className="request-activity-list">
                      {detailData.events.slice(0, 5).map((event) => (
                        <li key={event.id || `${event.action}-${event.created_at}`}>
                          <span aria-hidden="true" />
                          <div>
                            <strong>
                              {String(event.action || 'activity').replace(/_/g, ' ')}
                            </strong>
                            <time dateTime={toDateTimeAttribute(event.created_at)}>
                              {formatDateTime(event.created_at)}
                            </time>
                            {event.reason && <p>{event.reason}</p>}
                          </div>
                        </li>
                      ))}
                    </ol>
                  </section>
                )}
              </div>
            )}
          </div>

          <footer className="request-drawer-footer">
            {detailActionError && (
              <div className="request-drawer-action-error" role="alert">
                <AlertCircle aria-hidden="true" />
                <span>{detailActionError}</span>
              </div>
            )}
            <button
              type="button"
              className="request-action request-action-subtle"
              onClick={() => openBrdSource(pdfId)}
              disabled={!pdfId}
            >
              <Eye aria-hidden="true" />
              View source
            </button>
            {canTakeAction ? (
              renderActionButtons(verifiedItem, { drawer: true })
            ) : (
              <p>
                {detailLoading
                  ? 'Decision actions unlock after review details load.'
                  : detailError
                    ? 'Reload the review before taking action.'
                    : isAwaitingDecision && !hasActionContext
                      ? 'Decision details are incomplete for this request.'
                      : 'This request is no longer awaiting a decision.'}
              </p>
            )}
          </footer>
        </aside>
      </div>
    );
  };

  const renderRejectionDialog = () => {
    if (!rejectionItem) return null;
    const copy = getRejectDialogCopy(rejectionItem);
    const rejectKey = actionKeyFor(rejectionItem, 'reject');
    const isSubmitting = actionKey === rejectKey;
    const reasonMissing = copy.reasonRequired && !rejectionReason.trim();

    return (
      <div
        className="request-decision-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          if (event.target === event.currentTarget && !isSubmitting) {
            closeRejectionDialog();
          }
        }}
      >
        <section
          ref={rejectionDialogRef}
          className="request-decision-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="request-decision-title"
          aria-describedby="request-decision-copy"
          tabIndex={-1}
          onKeyDown={(event) => trapDialogFocus(event, rejectionDialogRef.current)}
        >
          <div className="request-decision-icon" aria-hidden="true">
            <XCircle />
          </div>
          <p className="request-decision-eyebrow">{copy.eyebrow}</p>
          <h2 id="request-decision-title">{copy.title}</h2>
          <p id="request-decision-copy">{copy.copy}</p>

          <div className="request-decision-context">
            <strong>{getRequestTitle(rejectionItem)}</strong>
            <span>{getRequestContext(rejectionItem)}</span>
          </div>

          <label className="request-decision-field">
            <span>{copy.fieldLabel}</span>
            <textarea
              ref={rejectionReasonRef}
              value={rejectionReason}
              disabled={isSubmitting}
              onChange={(event) => {
                setRejectionReason(event.target.value);
                if (rejectionError) setRejectionError(null);
              }}
              placeholder={copy.placeholder}
              rows={4}
              maxLength={1000}
              aria-invalid={Boolean(rejectionError)}
              aria-describedby={rejectionError ? 'request-decision-error' : undefined}
            />
            <small>{rejectionReason.length}/1000</small>
          </label>

          {rejectionError && (
            <div
              id="request-decision-error"
              className="request-decision-error"
              role="alert"
            >
              <AlertCircle aria-hidden="true" />
              {rejectionError}
            </div>
          )}

          <div className="request-decision-footer">
            <button
              type="button"
              className="request-decision-cancel"
              onClick={() => closeRejectionDialog()}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              className="request-decision-confirm"
              onClick={() => runRequestAction(
                rejectionItem,
                'reject',
                rejectionReason,
                'dialog'
              )}
              disabled={isSubmitting || reasonMissing}
              aria-busy={isSubmitting}
            >
              {isSubmitting
                ? <Loader2 className="animate-spin" aria-hidden="true" />
                : <XCircle aria-hidden="true" />}
              {isSubmitting ? 'Submitting…' : copy.submitLabel}
            </button>
          </div>
        </section>
      </div>
    );
  };

  return (
    <>
      <WorkspacePage
        theme="requests"
        sidebar={(
          <WorkspaceSidebar
            theme="requests"
            kicker="Request Center"
            title="Review every approval in one governed queue"
            description="Approve requirements, sharing access, and saved test cases with a complete decision trail."
            action={false}
            metrics={sidebarMetrics}
            footer={{
              title: 'Governed delivery',
              description: 'Review · Decide · Continue',
              icon: ShieldCheck,
            }}
          />
        )}
      >
        <WorkspaceStageCard
          kicker="Governance & approvals"
          title="Approval queue"
          description="Review pending artifacts and trace every completed decision."
          status={stageStatus}
          actions={(
            <>
              <WorkspaceBackButton
                onClick={closeRequestCenter}
                disabled={Boolean(actionKey)}
                title="Go back"
              />
              <button
                type="button"
                className="secondary-btn request-refresh-button"
                onClick={refreshEverything}
                disabled={loading || Boolean(actionKey)}
                aria-label="Refresh pending requests and request history"
              >
                {loading
                  ? <Loader2 className="animate-spin" aria-hidden="true" />
                  : <RefreshCw aria-hidden="true" />}
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </>
          )}
          contentClassName="request-center-content"
        >
          {actionMessage && (
            <div className="request-feedback request-feedback-success" role="status">
              <CheckCircle2 aria-hidden="true" />
              <span>{actionMessage}</span>
              <button
                type="button"
                onClick={() => setActionMessage(null)}
                aria-label="Dismiss confirmation"
              >
                <X aria-hidden="true" />
              </button>
            </div>
          )}

          {pageActionError && (
            <div className="request-feedback request-feedback-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>Couldn’t complete that decision. {pageActionError}</span>
              <button
                type="button"
                onClick={() => setPageActionError(null)}
                aria-label="Dismiss decision error"
              >
                <X aria-hidden="true" />
              </button>
            </div>
          )}

          {error && isCurrentFilterLoaded && (
            <div className="request-feedback request-feedback-error" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>Couldn’t refresh; showing the last loaded queue. {error}</span>
              <button
                type="button"
                onClick={() => setError(null)}
                aria-label="Dismiss refresh error"
              >
                <X aria-hidden="true" />
              </button>
            </div>
          )}

          {renderToolbar()}
          {renderPendingPanel()}
          {renderHistoryPanel()}
        </WorkspaceStageCard>
      </WorkspacePage>

      {renderDetailsDrawer()}
      {renderRejectionDialog()}
    </>
  );
};
